/*
 * bc-model-3d — an interactive product model that RECOLORS with the variant,
 * rendered as the FIRST item of the product media gallery and loaded only when
 * the shopper presses "View in 3D".
 *
 * Driven entirely by the product's `custom.model_3d` metafield (written by
 * docs/metafield-audit/migration/upload_model_3d.py), emitted into the page by
 * snippets/bc-product-3d-slide.liquid as a JSON config child:
 *
 *   {"src": "<GLB url in Shopify Files>",
 *    "alt": "MightyLock — interactive 3D model",
 *    "option": "Color",
 *    "materials": {"<glTF material name>": {"<option value>": "#hex", "*": "#hex"}},
 *    "view": {"direction": [0.85, -1, 0.55], "up": [0, 0, 1],
 *             "distance": 2.1, "fov": 35}}          // all optional
 *
 * "*" means "every value of that option" — that is how the MightyLock's two
 * laser-etch decals stay raw-aluminium silver on all four anodized bodies
 * (the laser ablates the dyed oxide and reveals bare metal, black included).
 * Nothing here is product-specific: another GLB is another metafield.
 *
 * THE OVERLAY CAN PUT A WEBBING IN IT. A hardware product that also carries
 * `custom.installed_diagram` has a set of baked GLBs of ITSELF WITH A WEBBING
 * THREADED THROUGH IT — one per compatible webbing, the same models the doc
 * panel's Compatible-webbings figure draws — and the expand overlay grows a
 * picker that swaps the stage between them and back to the bare device
 * ("Device only"), plus any DEVICE VIEW the config flags — the hardware alone
 * in some state worth seeing, the Double Pin's spacers fitted. The one stage,
 * the one WebGL context and the one recolor serve every model; see the
 * "installed-webbing figures" section below for the cache, and
 * snippets/bc-model-3d-installed.liquid for the config it reads.
 * Nothing about it exists on a product without that metafield.
 *
 * IN THE OVERLAY ONLY, on purpose. The inline slide is the hero product shot:
 * its poster is a still of the bare device, its thumbnail is that same picture,
 * and it cross-fades over the selected variant's photo. A figure mounted there
 * would disagree with all three — so `collapse` puts the device back, and the
 * slide is the device-only slide it always was.
 *
 * WHY THREE.JS AND NOT <model-viewer>:
 * This is the SAME viewer as the desk previews the parts-diagram tool writes
 * (tools/parts-diagram-tool/make_preview_html.py) — deliberately, so a part
 * behaves on the storefront exactly as it does in the file Jerry mails around.
 * The load-bearing behaviour is FREE TUMBLE: dragging rotates the MODEL about
 * the CAMERA's own screen axes (a quaternion premultiply onto a pivot), so
 * there is no pole to stick at and no upright the part snaps back to — a part
 * is a thing you turn over in your hands. model-viewer cannot do this at all:
 * its orbit is polar and hard-clamped to 0–180°, so it always stops you at the
 * top and the bottom. Shopify's own 3D pipeline is model-viewer plus chrome
 * (`model_viewer_tag` + `Shopify.loadFeatures('model-viewer-ui')`), applies only
 * to models added as PRODUCT MEDIA, and carries no scene-graph API — so it can
 * neither tumble freely nor recolor. Hence three.js, pinned (never floating) so
 * a library release cannot change the storefront without a deploy, from the same
 * CDN and at the same version as the previews.
 *
 * jsDelivr's `/+esm` endpoint is what lets a classic script `import()` the
 * addons at all: `examples/jsm/*` import the bare specifier `three`, which needs
 * an import map, and an import map cannot be added after the page's first module
 * script has started loading (Horizon ships several). `/+esm` rewrites that bare
 * specifier to the same pinned `/npm/three@<v>/+esm` URL we import ourselves, so
 * all three modules share ONE three instance — verified live on this storefront:
 * `new RoomEnvironment() instanceof THREE.Scene`.
 *
 * WHY THE VIEWER IS A MODULE SINGLETON THAT MOVES BETWEEN HOSTS:
 * The gallery renders the same media twice — a slideshow (mobile, and the whole
 * view for `carousel` presentation) and a grid `<ul>` (desktop `grid`
 * presentation) — with CSS displaying exactly one at a time, so the model needs
 * a host in each. On top of that Horizon REPLACES the entire <media-gallery>
 * element on every variant change (media-gallery.js `#handleProductSelect` →
 * `replaceWith`), and the quick-add modal morphs `main`. A canvas owned by any
 * one host would therefore lose its WebGL context and reload the GLB on a plain
 * color click, on a resize across 750px, and on every expand into the overlay.
 *
 * So exactly one renderer exists per page, living in a "stage" div that is
 * re-parented into whichever host is currently displayed — or into the overlay
 * while that is open. Hosts CLAIM the stage when they become visible (an
 * IntersectionObserver never fires for a `display: none` host, which is
 * precisely the "is this the live gallery view" test we want). Re-parenting a
 * canvas preserves its GL context, so no claim costs a GLB fetch.
 *
 * SHADOW DOM: children of a shadow root are invisible to Horizon's morph (see
 * assets/morph.js), so a claim survives quick-add and combined-listing morphs as
 * well. The poster is a light-DOM SIBLING of this element rather than a child of
 * it, so the quick-add modal can hide the viewer — and with it the GLB download
 * — while the slide still renders as an ordinary image slide; it is also the
 * no-JS, no-WebGL and failed-load fallback.
 *
 * GESTURES: the inline model is inert until the shopper explicitly ARMS it, so a
 * live camera can never eat the carousel swipe or the desktop scroll wheel by
 * surprise. Armed, it is the desk preview: free tumble (plain drag, one finger),
 * wheel and pinch zoom, and PAN — right-drag or shift-drag on a desktop, two
 * fingers on a touch screen, where the same finger pair spreads to zoom and
 * slides to pan, the way every map app reads that gesture. The overlay is
 * interactive the moment it opens.
 *
 * WHEN ANY OF IT STARTS: when the shopper presses "View in 3D". Nothing else.
 *
 * The chain this pulls is not small — ~184 KB brotli of three.js from a third
 * origin, then a WebGL context, then a PMREM environment bake, then a
 * half-megabyte GLB — and it is worth none of that to someone who came for the
 * photos. So the model slide IS the selected variant's photo (the poster the
 * snippet renders) until the badge is pressed, and boot() has exactly one
 * caller-facing entry point: `load()`, reached from that press and from the
 * expand control, both of which are the shopper asking in so many words. There
 * is no observer boot, no idle callback, no timer, no prefetch — a page with a
 * model on it and nobody pressing costs the same as a page without one.
 *
 * That is also what lets the model stand FIRST in the gallery again, where it
 * belongs: position and cost are no longer the same decision.
 *
 * The press, then, is a load with a spinner in it (`data-bc-model-3d-loading` on
 * the wrapper), and on the far side of it the viewer ARMS itself rather than
 * waiting to be pressed a second time — one press, one intention.
 *
 * The IntersectionObserver below survives all of that, doing the OTHER half of
 * its old job: once a stage exists, it is how the stage follows the visible
 * host across the slideshow/grid breakpoint. Pre-boot it is inert by design.
 *
 * NO GREY FLASH: the GLB ships in its raw CAD look. The stage is held at opacity
 * 0 until the selected variant's colors have been applied, so the raw look is
 * never painted.
 *
 * NO PHOTO FLASH EITHER: that first reveal is a 0.35s cross-fade — poster out,
 * stage in — and a CSS transition belongs to an ELEMENT, so a gallery Horizon
 * re-renders (every variant change replaces the whole <media-gallery>) arrives
 * with brand new poster and stage nodes and plays the whole cross-fade again,
 * from the new variant's photo back to the model that was already mounted and
 * already recolored. Nothing about the swap is slow — the stage is re-parented
 * synchronously inside `replaceWith`'s own custom-element reactions — it is the
 * animation that is wrong. So the transitions are switched OFF (`markInstant`)
 * from the first re-claim onward: the attributes then land in the same task the
 * gallery is inserted in, and a forced style flush is not a paint, so the swap
 * has no painted intermediate at all. The first reveal of the page still fades.
 */
(() => {
  if (customElements.get('bc-model-3d')) return;

  /**
   * Pinned three.js — same version and CDN as the desk previews
   * (tools/parts-diagram-tool/make_preview_html.py). See the file header for why
   * the addons come through `/+esm` rather than an import map.
   */
  const THREE_BASE = 'https://cdn.jsdelivr.net/npm/three@0.165.0';
  const MODULE_URLS = [
    `${THREE_BASE}/+esm`,
    `${THREE_BASE}/examples/jsm/loaders/GLTFLoader.js/+esm`,
    `${THREE_BASE}/examples/jsm/environments/RoomEnvironment.js/+esm`,
  ];

  /**
   * The meshopt decoder — what makes a `gltfpack -cc` GLB readable.
   *
   * The publish scripts (docs/metafield-audit/migration/upload_model_3d.py and
   * setup_installed_diagram.py) run every model through gltfpack before it goes
   * to Files, which quantizes the vertex data and compresses the buffers under
   * EXT_meshopt_compression — a 3.5 MB CAD export lands at ~800 KB, ~550 KB over
   * the wire. GLTFLoader ships no decoder for it: without `setMeshoptDecoder` a
   * file declaring the extension in `extensionsRequired` throws at parse time,
   * which here means the poster stays and the model never appears.
   *
   * PINNED TO THE ENCODER'S OWN VERSION, not merely to a version: gltfpack IS
   * meshoptimizer's CLI, one source tree and one version number, so 1.2.0 here
   * is the decoder for the 1.2.0 that packed the bytes. (The file is
   * `meshopt_decoder.mjs` since 1.0 — 0.x spelled it `meshopt_decoder.module.js`,
   * which is what most three.js examples still name.)
   *
   * No `/+esm` on this one, unlike the three.js addons above: that suffix exists
   * to rewrite their bare `three` import, and this module imports nothing at all,
   * so the pinned file is already the ES module we want and jsDelivr's transform
   * would only add a hop.
   *
   * ATTACHING IT IS A NO-OP ON AN UNCOMPRESSED GLB — the decoder is reached only
   * through the extension — so this file renders both revisions, which is what
   * lets the theme ship before the data flips to the compressed urls.
   */
  const MESHOPT_URL = 'https://cdn.jsdelivr.net/npm/meshoptimizer@1.2.0/meshopt_decoder.mjs';

  /**
   * The opening pose, as a direction from the part toward the camera plus a
   * screen-up vector, both in the model's OWN frame — the same pair
   * tools/parts-diagram-tool/render_glb.py's VIEWS table uses.
   *
   * BOTH VALUES WERE CALIBRATED AGAINST THE RENDERED RESULT, not derived on
   * paper, because render_glb.py's sign conventions do not survive the trip into
   * a three.js camera — read this before "fixing" the signs:
   *
   *   - The etched FRONT of a MightyLock registers on its native y = 13 face
   *     (worked out from the O5 keeper hole rather than declared — see
   *     make_mightylock_glb.py's ML_ETCH_FACE), so the camera sits on +Y.
   *     render_glb.py's own `hero`, (0.85, -1, 0.55), puts it on -Y here and
   *     shows the y = 0 stats face instead.
   *   - The part's DISPLAY up — the bar carrying the wordmark — is native -Z,
   *     hence up = (0, 0, -1). With +Z the whole view comes out rolled 180°:
   *     wordmark along the bottom, upside down, keeper hole bottom-right.
   *
   * The result is the reference render Jerry supplied: the front face square to
   * the camera and upright (no roll, up being exactly an axis), yawed ~25° so
   * the frame's right-hand side reads as a surface with the near side on the
   * right, and tipped ~8° so the part is seen from very slightly above. Per
   * product overridable through the metafield's `view` object.
   */
  const DEFAULT_DIRECTION = [0.42, 0.9, -0.13];
  const DEFAULT_UP = [0, 0, -1];
  const DEFAULT_DISTANCE = 1.6;
  const DEFAULT_FOV = 35;

  /**
   * The device's own GLB — `custom.model_3d`'s `src`, the only model this file
   * loaded until the installed-webbing figures arrived. Keyed like any other so
   * one cache, one mount path and one "what is on stage" answer serve both.
   *
   * It is PINNED in that cache: "Device only" is the way back out of a figure
   * and has to be instant, and it is the model the overlay collapses to.
   */
  const DEVICE_KEY = '';

  /**
   * How many PARSED models stay resident: the device plus the two figures most
   * recently picked. Comparing is what the picker is for — A→B→A must not
   * refetch — while a full roster is a dozen baked assemblies, and a dozen
   * resident WebGL scenes is not a thing to leave behind on a product page.
   * Same cap, same reasoning, as assets/bc-installed-diagram.js.
   */
  const MODEL_CACHE_LIMIT = 3;

  /**
   * The decoder, ready to hand to a loader — or null, which every caller reads
   * as "parse without it".
   *
   * Its WASM is instantiated HERE rather than at the first parse: `ready` is
   * awaited so the module's compile overlaps the three.js download and the GLB
   * fetch, both of which are already in flight, instead of landing in front of
   * the parse. (GLTFLoader awaits `ready` itself, so this is a scheduling choice,
   * not a correctness one.)
   *
   * It FAILS SOFT — a CDN hiccup, or a browser with no WebAssembly, resolves to
   * null rather than rejecting — because rejecting would take the viewer down
   * with it on the uncompressed models that never needed a decoder. A compressed
   * GLB with no decoder still fails, loudly and in the right place: GLTFLoader
   * throws at parse and boot() drops to the poster.
   */
  const loadMeshopt = () =>
    import(MESHOPT_URL).then(
      async (mod) => {
        const decoder = mod?.MeshoptDecoder;
        if (!decoder?.supported) return null;
        await decoder.ready;
        return decoder;
      },
      () => null
    );

  /** One import per page, shared by every host. */
  let libPromise = null;
  const loadLib = () =>
    (libPromise ||= Promise.all([...MODULE_URLS.map((url) => import(url)), loadMeshopt()]));

  /**
   * The GLB, fetched as bytes so it can run ALONGSIDE the library import instead
   * of behind it.
   *
   * `GLTFLoader.load(url)` cannot start until GLTFLoader exists, which stacks a
   * half-megabyte transfer on top of the three.js download, the WebGL context
   * and the environment bake — all serial, all before the first frame. The URL,
   * though, is known from the metafield at boot time, and it is a different
   * origin from the library, so there is no reason for the two to queue. The
   * bytes are handed to `GLTFLoader.parse` instead, which is the same code path
   * `load` reaches once its own fetch completes.
   */
  const fetchModel = (url) =>
    fetch(url).then((response) => {
      if (!response.ok) throw new Error(`bc-model-3d: GLB request failed (${response.status})`);
      return response.arrayBuffer();
    });

  /** The directory the GLB sits in — what `load()` derives for relative resources. */
  const resourcePath = (url) => url.slice(0, url.lastIndexOf('/') + 1);

  const state = {
    /* -- the installed-webbing figures (see the file header) ---------------- *
     * Everything here stays null/empty on a product with no
     * `custom.installed_diagram`: nothing below runs, and the gallery is the
     * device-only one it has always been.
     */
    /** @type {any} the config emitted by snippets/bc-model-3d-installed.liquid */ installed: null,
    /** @type {string|null} the picked webbing handle, null = device only */ webbing: null,
    /** @type {string|null} the picked DEVICE VIEW's model key, null = the bare device */ deviceView: null,
    /** Which model is mounted. @type {string} */ modelKey: DEVICE_KEY,
    /** Parsed, mounted-or-cached models by key. @type {Map<string, any>} */ entries: new Map(),
    /** In-flight GLB bytes — a HANDOFF, not a cache. @type {Map<string, Promise<ArrayBuffer>>} */ glbBytes: new Map(),
    /** In-flight parses. @type {Map<string, Promise<any>>} */ parsing: new Map(),
    /** LRU order, most recent last. @type {string[]} */ order: [DEVICE_KEY],
    /** Bumped by every pick, so a slow GLB can never land under a newer one. */ showToken: 0,
    /** @type {HTMLElement|null} the div holding the canvas, moved between hosts and the overlay */ stage: null,
    /** @type {any} */ viewer: null,
    /** @type {any} */ config: null,
    /** @type {HTMLElement|null} */ host: null,
    /** @type {HTMLDialogElement|null} */ dialog: null,
    /** @type {Promise<any>|null} */ bootPromise: null,
    expanded: false,
    armed: false,
    /** No WebGL, or a GLB that would not load — see setErrorState. */ failed: false,
    /**
     * The hosts an IntersectionObserver currently calls on screen.
     * @type {Set<Element>}
     *
     * A SET rather than a flag because there are two hosts (slideshow and
     * grid), each with its own observer, and the one that is `display: none`
     * never intersects — so a single boolean written by whichever callback ran
     * last would be the hidden host's answer half the time. A host is added
     * optimistically when it starts being observed and removed by its own first
     * callback if it turns out not to be visible, which is what keeps the very
     * first frame from waiting on an observer.
     */
    onScreenHosts: new Set(),
    /** The shopper's place in the carousel, so a gallery swap can land where they were. */ lastSlideIndex: 0,
  };

  const parseVec = (value, fallback) => {
    const parts = String(value ?? '')
      .split(',')
      .map((n) => Number(n.trim()));
    return parts.length === 3 && parts.every((n) => Number.isFinite(n)) ? parts : fallback;
  };

  /** The same thing for a JSON `view`, whose vectors arrive as arrays. */
  const asVec = (value, fallback) => {
    const parts = Array.isArray(value) ? value.map(Number) : [];
    return parts.length === 3 && parts.every((n) => Number.isFinite(n)) ? parts : fallback;
  };

  /**
   * One authored view, normalised — a direction from the part toward the camera
   * plus a screen-up vector, both in the model's own (Z-up CAD) frame, plus its
   * framing. The device's arrives as dataset strings, a figure's as JSON.
   */
  const viewFrom = (view) => ({
    direction: asVec(view?.direction, DEFAULT_DIRECTION),
    up: asVec(view?.up, DEFAULT_UP),
    distance: Number(view?.distance) || DEFAULT_DISTANCE,
    fov: Number(view?.fov) || DEFAULT_FOV,
  });

  /**
   * A parsed GLB, turned into everything the stage needs to mount it: the scene
   * centred on its own origin, its colourable materials (as a list for the
   * anodize recolor and by NAME for the webbing paint), its opening pose and the
   * scale its framing is derived from.
   *
   * THE POSE, and why the signs are what they are. render_glb.py names a view by
   * the direction from the part toward the camera plus a screen-up vector, both
   * in the model's own frame; its `camera_axes` turns that pair into the
   * model-space vectors that map to screen right / up / toward-viewer, and the
   * rotation we want takes those three onto the world X / Y / Z the camera sits
   * on — that basis, transposed. It is applied to the MODEL rather than the
   * camera so it is the same pivot rotation a drag produces: tumbling from here
   * is continuous, and "reset" is just this quaternion again.
   *
   * Both device defaults were calibrated against the rendered result, not
   * derived on paper — read the note on DEFAULT_DIRECTION before "fixing" a
   * sign. A figure's own `view` comes from the metafield and transfers verbatim.
   */
  const makeEntry = (THREE, gltf, view) => {
    const model = gltf.scene;
    const box = new THREE.Box3().setFromObject(model);
    model.position.sub(box.getCenter(new THREE.Vector3()));
    const size = box.getSize(new THREE.Vector3()).length() || 1;

    /** @type {any[]} */
    const materials = [];
    /** @type {Map<string, any>} */
    const byName = new Map();
    const seen = new Set();
    model.traverse((node) => {
      const material = /** @type {any} */ (node).material;
      if (!material) return;
      for (const entry of Array.isArray(material) ? material : [material]) {
        if (!entry?.color || seen.has(entry)) continue;
        seen.add(entry);
        materials.push(entry);
        if (entry.name && !byName.has(entry.name)) byName.set(entry.name, entry);
        // An installed figure carries the webbing's printed cross-section as
        // skins standing a few microns off the solid body named `webbing`.
        // Zoomed out that is under the depth buffer's resolution and the body
        // fights through the skins — purple streaks across a green face on a
        // two-tone webbing. Push the BODY back two depth units so the skins
        // always win; nothing else in the model moves, and the device GLB
        // carries no such material, so this costs it nothing.
        if (entry.name === 'webbing') {
          entry.polygonOffset = true;
          entry.polygonOffsetFactor = 1;
          entry.polygonOffsetUnits = 2;
          entry.needsUpdate = true;
        }
      }
    });

    const zDir = new THREE.Vector3(...view.direction).normalize();
    const xDir = new THREE.Vector3(...view.up).cross(zDir).normalize();
    const yDir = new THREE.Vector3().crossVectors(zDir, xDir);
    const home = new THREE.Quaternion().setFromRotationMatrix(
      new THREE.Matrix4().makeBasis(xDir, yDir, zDir).transpose()
    );

    return { model, materials, byName, home, size, distance: view.distance, fov: view.fov };
  };

  /**
   * The host the stage belongs in RIGHT NOW: the one actually being displayed.
   * Which of the slideshow and grid hosts that is flips at the gallery's 750px
   * breakpoint, and during first paint — before the stylesheet has applied —
   * both look displayed, so this is re-evaluated after every await rather than
   * latched when a claim starts.
   */
  const visibleHost = () => {
    const hosts = [...document.querySelectorAll('bc-model-3d')];
    return (
      hosts.find((el) => (el.checkVisibility ? el.checkVisibility() : el.offsetParent !== null)) ||
      (state.host?.isConnected ? state.host : null) ||
      hosts[0] ||
      null
    );
  };

  /**
   * Is there anywhere on screen for the one viewer to draw to — a host near the
   * viewport, or the overlay open — with the tab in front?
   *
   * The overlay outranks every host: its stage is re-parented onto a <dialog>
   * in the top layer, so the gallery it came from may be anywhere at all.
   */
  const loopWanted = () =>
    !state.failed && (state.expanded || state.onScreenHosts.size > 0) && document.visibilityState !== 'hidden';

  /**
   * Start or stop the render loop to match. Called from the observers, from
   * connect/disconnect, from expand/collapse, from visibilitychange and from
   * every invalidate — anything that can change the answer above.
   *
   * Stopping is `setAnimationLoop(null)` and nothing else: the renderer, the
   * WebGL context, the baked environment and the parsed model all stay, which
   * is what lets the single stage go on being re-parented between the two hosts
   * and the overlay with nothing to rebuild.
   */
  const syncLoop = () => state.viewer?.setLoopRunning(loopWanted());

  /** Record an observer's answer for one host and re-decide the loop. */
  const setHostOnScreen = (host, on) => {
    if (on) state.onScreenHosts.add(host);
    else state.onScreenHosts.delete(host);
    syncLoop();
  };

  /** Every `.bc-product-3d` wrapper on the page — the state-flag carriers for CSS. */
  const setRootFlag = (name, on) => {
    for (const root of document.querySelectorAll('[data-bc-model-3d-root]')) {
      if (on) root.setAttribute(name, '');
      else root.removeAttribute(name);
    }
  };

  /**
   * Selected value of the watched option, read from the MAIN product form via
   * the shared resolver (assets/bc-variant-resolve.js) exactly as
   * bc-variant-recolor does — quick-add-dialog pickers excluded, because a modal
   * selection is a different product context. Falls back to the value Liquid
   * rendered for the initially selected variant, which also covers the case
   * where the resolver asset hasn't parsed yet.
   */
  const selectedValue = (optionName) => {
    const resolved = optionName
      ? window.bcVariantResolve?.readSelectedOption(document, optionName, { exclude: 'quick-add-dialog' })
      : null;
    if (resolved) return resolved;
    return state.host?.dataset.initialValue || null;
  };

  /**
   * Paint the selected variant's colors onto the model's materials, matched BY
   * NAME exactly as the metafield contract says. A material the GLB doesn't
   * carry, or an option value with no entry, is left exactly as authored — an
   * unknown selection never throws and never half-repaints the model.
   *
   * `Color.set('#rrggbb')` is the right call rather than any manual conversion:
   * three 0.165 has ColorManagement on by default, so it reads the hex as sRGB
   * and converts into the linear working space, and the renderer's sRGB output
   * converts back — the pixel matches the hex, the way the desk previews do.
   */
  const recolor = () => {
    const viewer = state.viewer;
    const map = state.config?.materials;
    if (!viewer?.materials?.length || !map) return false;

    const value = selectedValue(state.config.option);
    let painted = false;

    for (const material of viewer.materials) {
      const colors = map[material.name];
      if (!colors) continue;
      const hex = (value != null && colors[value]) || colors['*'];
      if (typeof hex !== 'string' || !hex) continue;
      try {
        material.color.set(hex);
        painted = true;
      } catch (_e) {
        /* leave this material as authored */
      }
    }

    if (painted) viewer.invalidate();
    return painted;
  };

  /**
   * Paint the picked webbing's own diagram colour onto the mounted figure's
   * webbing material(s) — the other half of the recolor, and the same contract
   * assets/bc-installed-diagram.js paints its figure by.
   *
   * Only a GENERIC figure is painted, and the config says which: a `hex` is
   * emitted for a webbing landing on a shared model and withheld for one with a
   * BAKED model of its own, whose strip already carries its printed
   * cross-section in several materials — flooding one of them with the line
   * colour would erase the design. The device model has no webbing in it and is
   * never reached (no `webbing` picked → nothing to paint).
   */
  const paintWebbing = () => {
    const viewer = state.viewer;
    const entry = viewer?.entry;
    const hex = state.webbing ? state.installed?.webbings?.[state.webbing]?.hex : null;
    if (!entry || typeof hex !== 'string' || !hex) return;
    for (const name of state.installed?.webbingMaterials || []) {
      const material = entry.byName?.get(name);
      if (!material) continue;
      try {
        material.color.set(hex);
      } catch (_e) {
        /* leave this material as authored */
      }
    }
    viewer.invalidate();
  };

  /**
   * `data-bc-state="ready"` fades the poster out and the affordance in, so it
   * belongs ONLY to the host that actually holds the stage (plus, while the
   * overlay is open, the host it will return to). Marking both the slideshow and
   * the grid host would leave whichever is currently `display: none` posterless
   * — and blank for a frame when a resize across 750px makes it the visible one.
   */
  const syncHostStates = () => {
    const ready = Boolean(state.viewer?.ready);
    for (const el of document.querySelectorAll('bc-model-3d')) {
      if (el.dataset.bcState === 'error') continue;
      const holds = ready && (el.shadowRoot?.contains(state.stage) || (state.expanded && el === state.host));
      if (holds) el.dataset.bcState = 'ready';
      else delete el.dataset.bcState;
    }
  };

  /**
   * Retires the poster/stage cross-fade, on EVERY host.
   *
   * That fade is for one moment only — the first time the model paints over its
   * poster — and it is per-element, so it plays again on every gallery Horizon
   * re-renders (see the file header). `data-bc-instant` is read by the shadow
   * stylesheet below and by `.bc-product-3d__poster` in brand.css, and it is
   * only ever set once a live, loaded viewer exists, so the page's own first
   * reveal keeps its fade.
   *
   * Every host, not just the claiming one, because `visibleHost()` calls
   * `checkVisibility()` — which flushes style — and the host that goes on to
   * hold the stage may not have run its `connectedCallback` yet at that point.
   * An unmarked host caught by that flush would still have a transition to run.
   */
  const markInstant = () => {
    for (const el of document.querySelectorAll('bc-model-3d')) el.dataset.bcInstant = '';
  };

  /** A failed load, or a machine with no WebGL: every host keeps its poster. */
  const setErrorState = () => {
    state.failed = true;
    for (const el of document.querySelectorAll('bc-model-3d')) el.dataset.bcState = 'error';
    setRootFlag('data-bc-model-3d-failed', true);
    // No host will ever reveal the stage now (syncHostStates leaves an `error`
    // element alone, so every poster stays), which makes this the one true
    // teardown here: setAnimationLoop(null), and loopWanted never says yes
    // again. A viewer that got as far as its own context but not as far as a
    // model — a GLB that 404s — would otherwise keep a 60 fps callback alive
    // behind a poster for the rest of the session.
    syncLoop();
  };

  /* ------------------------------------------------------------------ *
   * The viewer itself — make_preview_html.py's, plus what a PDP needs that
   * a full-window desk preview did not: a sized container instead of the
   * window, pinch zoom, a DPR cap, and render-on-demand.
   * ------------------------------------------------------------------ */

  const buildViewer = async (host) => {
    // Both requests leave now, in parallel — see fetchModel. The rejection is
    // handled properly further down (`bytes.then(…, reject)`); this no-op
    // handler only stops a library failure, which would abandon the chain before
    // that point, from surfacing the GLB's own failure as an unhandled rejection.
    const bytes = fetchModel(state.config.src);
    bytes.catch(() => {});

    const [THREE, gltfMod, roomMod, meshopt] = await loadLib();

    const stage = document.createElement('div');
    stage.className = 'bc-model-3d-stage';

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch (_e) {
      return null; // no WebGL — the poster stays, see setErrorState
    }
    // Retina is not worth 4x the fragments on a product-photo-sized canvas.
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.domElement.setAttribute('aria-hidden', 'true');
    stage.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    // No scene.background: the desk preview sits on its own dark page, this one
    // has to sit on whatever the gallery's background is.
    try {
      scene.environment = new THREE.PMREMGenerator(renderer).fromScene(new roomMod.RoomEnvironment(), 0.04).texture;
    } catch (_e) {
      /* the environment is a nicety; the explicit lights below carry the scene */
    }
    scene.add(new THREE.HemisphereLight(0xffffff, 0x444455, 1.2));
    const key = new THREE.DirectionalLight(0xffffff, 1.6);
    key.position.set(60, 80, 120);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xffffff, 0.6);
    fill.position.set(-80, -40, -100);
    scene.add(fill);

    const camera = new THREE.PerspectiveCamera(Number(host.dataset.fov) || DEFAULT_FOV, 1, 0.01, 2000);
    let dist = 1;
    let minDist = 0.1;
    let maxDist = 100;
    // How far the model may be slid off centre — see applyFraming.
    let panLimit = Infinity;
    camera.position.set(0, 0, dist);

    const pivot = new THREE.Group();
    scene.add(pivot);

    /**
     * The opening pose of whatever is MOUNTED — the hero three-quarter of
     * render_glb.py's VIEWS table for the device, a figure's own authored view
     * when one is on stage. Built by makeEntry, which carries the note on how
     * the pair of vectors becomes this quaternion; assigned by `mount`, which is
     * why it is a `let`: "home" means the pose of the model you are looking at.
     */
    let homeQuaternion = new THREE.Quaternion();

    let dirty = true;

    /**
     * A frame is WANTED. Every "something moved" in this viewer goes through
     * here rather than assigning `dirty` directly, because the loop is not
     * always running (see setLoopRunning): the flag on its own would be a
     * repaint that never happens. Starting it is conditional on there being
     * somewhere to draw to, so an invalidate from off screen (a variant recolor
     * with the gallery scrolled past) sets the flag and leaves the loop stopped.
     */
    const mark = () => {
      dirty = true;
      syncLoop();
    };

    /** Is the rAF callback installed? See setLoopRunning. */
    let looping = false;

    const viewer = {
      THREE,
      /** The library handles, so a model picked LATER can be parsed out here. */
      lib: { THREE, gltfMod, meshopt },
      stage,
      renderer,
      scene,
      camera,
      pivot,
      /** The MOUNTED model's colourable materials — what recolor() paints. @type {any[]} */ materials: [],
      /** @type {any} the mounted entry (see makeEntry) */ entry: null,
      ready: false,
      homeDist: 1,
      autoRotate:
        host.dataset.autoRotate === 'true' && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
      invalidate: mark,
      /**
       * Install or drop the rAF callback.
       *
       * Render-on-demand already keeps the GPU idle, but the CALLBACK is not
       * free: it runs 60 times a second for the life of the page, and this
       * viewer is deliberately built to outlive every gallery Horizon
       * re-renders — so "the life of the page" is exactly what it means.
       *
       * The clock is read and thrown away on the way in: `getDelta` measures
       * from the last read, so an idle stretch would otherwise arrive at the
       * auto-rotate as one enormous delta and snap the model round.
       */
      setLoopRunning(on) {
        if (on === looping) return;
        looping = on;
        if (!on) {
          renderer.setAnimationLoop(null);
          return;
        }
        clock.getDelta();
        renderer.setAnimationLoop(frame);
      },
      home() {
        pivot.quaternion.copy(homeQuaternion);
        viewer.homeFraming();
      },
      /**
       * FRAMING only — distance and pan — orientation untouched. That is what
       * collapsing out of the overlay wants: a shopper who zoomed right in on
       * the etch there should not get that crop back in a slide a third of the
       * size, but the face they turned toward themselves is theirs to keep.
       *
       * Pan goes with the distance rather than with the orientation because it
       * is the same kind of thing: where the part is CROPPED, not which way it
       * faces. It also has to. A pan is a world offset sized by the overlay's
       * pixels, and the slide it collapses into is a third of that; keeping one
       * could hand back a slide with the model entirely outside it. Reframing
       * both together means the collapse always lands centred and whole.
       */
      homeFraming() {
        dist = viewer.homeDist;
        camera.position.set(0, 0, dist);
        pivot.position.set(0, 0, 0);
        mark();
      },
      /**
       * Put a parsed model on the stage, in place of whatever was there.
       *
       * The pivot, the camera, the lights and the baked environment all stay —
       * a swap is one `remove` and one `add`, which is what makes going back to
       * "Device only" a frame rather than a rebuild. Framing is re-derived
       * because it has to be: these GLBs range from a 46 mm lock to a 220 mm
       * assembly with a webbing loop through it.
       *
       * `rehome` is the ONE judgment call in the swap. Between two webbings it
       * is false — same assembly, another strap, and snapping the view back
       * would read as the figure resetting itself under the shopper. Between the
       * device and a figure it is true: a different subject, authored to be seen
       * from its own angle, and the two poses have no relation to each other.
       */
      mount(entry, rehome) {
        const previous = viewer.entry;
        if (previous === entry) return;
        if (previous) pivot.remove(previous.model);
        pivot.add(entry.model);
        viewer.entry = entry;
        viewer.materials = entry.materials;
        homeQuaternion = entry.home;
        if (rehome || !previous) pivot.quaternion.copy(entry.home);
        applyFraming(entry);
        mark();
      },
      resize() {
        const width = stage.clientWidth;
        const height = stage.clientHeight;
        if (!width || !height) return;
        // updateStyle left ON deliberately: the canvas is sized in CSS pixels
        // by the renderer itself, so it is correct in the shadow root AND in the
        // overlay, where the shadow stylesheet does not reach.
        renderer.setSize(width, height);
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        // Redraw HERE rather than flagging the animation loop: setSize
        // reallocates — and so clears — the drawing buffer, and the
        // ResizeObserver that calls this runs AFTER the frame's rAF callbacks,
        // so a deferred redraw would let one frame paint an empty canvas (and,
        // mid gallery swap, the poster showing through it).
        dirty = false;
        renderer.render(scene, camera);
      },
      zoomBy(factor) {
        dist = Math.min(maxDist, Math.max(minDist, dist * factor));
        camera.position.setLength(dist);
        mark();
      },
      /**
       * Pan, in CANVAS PIXELS — the model slides in the camera's screen plane
       * and the pointer keeps hold of the point it grabbed.
       *
       * Moving the PIVOT rather than the camera, for the same reason tumbling
       * turns the model: it leaves the camera on the z axis, so `zoomBy` stays
       * one `setLength`, `resize` stays an aspect update, and reframing stays
       * two assignments. It also means screen right and up ARE world +X and +Y
       * (the camera is never rotated), so there is no basis to build here.
       *
       * The scale is the view's own: a perspective camera sees
       * `2 * dist * tan(fov / 2)` of world across the canvas height at the
       * distance the model sits at, so that over the height in pixels is what
       * one pixel is worth — which is what makes the model track the pointer
       * 1:1. Zoomed in, the same drag moves it less in world units, exactly as
       * the shopper expects.
       *
       * Tumbling still turns the model about its own centre, wherever that
       * centre has been slid to, because the pivot rotates about its origin.
       */
      panBy(dxPixels, dyPixels) {
        const perPixel = (2 * dist * Math.tan((camera.fov * Math.PI) / 360)) / (renderer.domElement.clientHeight || 1);
        // Screen y runs down, world y runs up.
        pivot.position.set(pivot.position.x + dxPixels * perPixel, pivot.position.y - dyPixels * perPixel, 0);
        const offset = Math.hypot(pivot.position.x, pivot.position.y);
        if (offset > panLimit) pivot.position.multiplyScalar(panLimit / offset);
        mark();
      },
      tumble(dx, dy) {
        const q = new THREE.Quaternion()
          .setFromAxisAngle(new THREE.Vector3(0, 1, 0), dx)
          .multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), dy));
        pivot.quaternion.premultiply(q);
        mark();
      },
    };

    // Scale-aware framing: these GLBs are in metres (a 52 mm part is 0.052
    // across) and range from a 46 mm lock to a 220 mm assembly, so the distance,
    // the clipping planes and the zoom limits are all derived from the model —
    // which is why it is re-run by every `mount` rather than once at boot.
    const applyFraming = (entry) => {
      const size = entry.size;
      dist = size * entry.distance;
      viewer.homeDist = dist;
      minDist = size * 0.4;
      maxDist = size * 12;
      // A pan can slide the model 1.5 bounding RADII off centre and no further,
      // so it can never be lost off the canvas. `size` is the bounding box's
      // DIAGONAL, hence half of it as the radius: 1.5 x size / 2 = 0.75 x size.
      // Zoomed right in that is a long way in screen terms — well past the far
      // side of the part — and zoomed out it is a fraction of the frame, which
      // is the correct feel in both.
      panLimit = size * 0.75;
      camera.near = size / 100;
      camera.far = size * 100;
      camera.fov = entry.fov;
      camera.updateProjectionMatrix();
      camera.position.set(0, 0, dist);
      pivot.position.set(0, 0, 0);
    };

    const loaded = new Promise((resolve, reject) => {
      const onLoad = (gltf) => {
        const entry = makeEntry(
          THREE,
          gltf,
          viewFrom({
            direction: parseVec(host.dataset.viewDirection, DEFAULT_DIRECTION),
            up: parseVec(host.dataset.viewUp, DEFAULT_UP),
            distance: host.dataset.distance,
            fov: host.dataset.fov,
          })
        );
        state.entries.set(DEVICE_KEY, entry);
        viewer.mount(entry, true);
        resolve(entry);
      };

      // Whatever went wrong — the fetch, the parse, a malformed GLB — comes out
      // as one rejection, which boot() turns into the same poster-only fallback
      // a loader error always produced.
      bytes.then((buffer) => {
        try {
          const loader = new gltfMod.GLTFLoader();
          // Only a GLB that declares EXT_meshopt_compression ever reaches it, so
          // this costs the uncompressed models nothing (see MESHOPT_URL).
          if (meshopt) loader.setMeshoptDecoder(meshopt);
          loader.parse(buffer, resourcePath(state.config.src), onLoad, reject);
        } catch (error) {
          reject(error);
        }
      }, reject);
    });

    // A ResizeObserver rather than a window listener: it also covers the move
    // into and out of the expand overlay, where the window never changes size.
    new ResizeObserver(() => viewer.resize()).observe(stage);

    const clock = new THREE.Clock();
    const frame = () => {
      const delta = clock.getDelta();
      // Render-on-demand: the loop runs, the GPU only works when something
      // moved. The desk preview can afford to redraw a full window forever; a
      // product page cannot — and the one thing that moves on its own is
      // skipped entirely while no host is on screen, so an idle spin cannot
      // burn a phone's battery half a page above the shopper. (Belt and braces
      // with setLoopRunning, which stops the loop outright in that case; this
      // covers the frames between the gallery leaving and its observer saying
      // so.)
      if (viewer.autoRotate && state.onScreenHosts.size > 0 && !state.armed && !state.expanded) {
        pivot.rotateOnWorldAxis(new THREE.Vector3(0, 1, 0), delta * 0.35);
        // Straight to the flag: the loop is running by definition in here, so
        // going through mark() would ask to start it once a frame forever.
        dirty = true;
      }
      if (!dirty) return;
      dirty = false;
      renderer.render(scene, camera);
    };
    viewer.setLoopRunning(true);

    wireGestures(viewer);

    state.stage = stage;
    state.viewer = viewer;
    // Only now can the page's own answer be applied — setLoopRunning above
    // started the loop against a null state.viewer, which is what draws the
    // first frame; this stops it again if the gallery has been scrolled past or
    // the tab backgrounded mid-load.
    syncLoop();

    await loaded;
    viewer.ready = true;
    viewer.resize();
    return viewer;
  };

  /**
   * Pointer handling, straight from the desk preview plus two-finger pinch.
   * Everything is gated on `interactive()` so an un-armed inline model cannot
   * swallow a carousel swipe, a page scroll or a right-click.
   */
  const interactive = () => state.expanded || state.armed;

  const wireGestures = (viewer) => {
    const canvas = viewer.renderer.domElement;
    /** @type {Map<number, {x: number, y: number}>} */
    const pointers = new Map();
    let pinchDistance = 0;
    let midX = 0;
    let midY = 0;

    /** The two pointers' separation — how far apart the pinch is. */
    const spread = () => {
      const [a, b] = [...pointers.values()];
      return Math.hypot(a.x - b.x, a.y - b.y);
    };

    /** And their midpoint — the point a two-finger drag carries the model by. */
    const midpoint = () => {
      const [a, b] = [...pointers.values()];
      return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    };

    /**
     * A one-pointer drag pans instead of tumbling while the RIGHT button is
     * down (`buttons` is a bitmask of what is pressed) or Shift is held. Two
     * ways in because plenty of shoppers are on a trackpad, where a right-drag
     * is a two-handed contortion and a shift-drag is not.
     *
     * It LATCHES for the rest of the drag. Testing the live event alone would
     * be less state, but a shift-drag actually ENDS by letting go of Shift a
     * moment before the button — which would spin the model on the last few
     * pixels of every pan. So: once a drag pans it pans to the end, while a
     * tumble can still be turned into a pan by taking the modifier up mid-drag.
     */
    let panDrag = false;
    const panning = (event) => {
      panDrag ||= Boolean(event.buttons & 2) || event.shiftKey;
      return panDrag;
    };

    canvas.addEventListener('pointerdown', (event) => {
      if (!interactive()) return;
      canvas.setPointerCapture(event.pointerId);
      if (pointers.size === 0) panDrag = false; // a new drag decides afresh
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      // The modifier is read from the press as well as from the moves, so a
      // gesture that began as a pan is one even if the moves arrive bare.
      panning(event);
      if (pointers.size === 2) {
        pinchDistance = spread();
        ({ x: midX, y: midY } = midpoint());
      }
    });

    canvas.addEventListener('pointermove', (event) => {
      if (!interactive() || !pointers.has(event.pointerId)) return;
      const previous = pointers.get(event.pointerId);
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      // Two fingers are ONE gesture read two ways, both off the same pair: what
      // the spread does is zoom, what the midpoint does is pan. Separating them
      // is what makes a pinch that drifts, or a drag that opens slightly, feel
      // like the map app it is imitating rather than like a mode switch.
      if (pointers.size >= 2) {
        const next = spread();
        const mid = midpoint();
        if (pinchDistance > 0 && next > 0) viewer.zoomBy(pinchDistance / next);
        pinchDistance = next;
        viewer.panBy(mid.x - midX, mid.y - midY);
        midX = mid.x;
        midY = mid.y;
        return;
      }

      if (panning(event)) {
        viewer.panBy(event.clientX - previous.x, event.clientY - previous.y);
        return;
      }

      // Radians-ish per canvas height, so the feel is size-independent.
      const scale = 3.2 / (canvas.clientHeight || 1);
      viewer.tumble((event.clientX - previous.x) * scale, (event.clientY - previous.y) * scale);
    });

    const release = (event) => {
      pointers.delete(event.pointerId);
      if (pointers.size < 2) pinchDistance = 0;
      if (pointers.size === 0) panDrag = false;
    };
    canvas.addEventListener('pointerup', release);
    canvas.addEventListener('pointercancel', release);

    // A right-drag is a pan, so the menu would land at the end of every one.
    // Gated like every other gesture: an un-armed inline canvas is a picture,
    // and a picture gets the browser's own menu (save image, and the rest).
    canvas.addEventListener('contextmenu', (event) => {
      if (!interactive()) return;
      event.preventDefault();
    });

    // Exponential, so the perceived step is the same 50 mm or 500 mm out, and it
    // can never cross zero. Not passive: an armed model owns the wheel.
    canvas.addEventListener(
      'wheel',
      (event) => {
        if (!interactive()) return;
        event.preventDefault();
        viewer.zoomBy(Math.exp(event.deltaY * 0.0007));
      },
      { passive: false }
    );
  };

  /* ------------------------------------------------------------------ *
   * Arming — the inline model is a picture until the shopper says otherwise.
   * ------------------------------------------------------------------ */

  const syncTouchAction = () => {
    const canvas = state.viewer?.renderer.domElement;
    if (canvas) canvas.style.touchAction = interactive() ? 'none' : '';
  };

  const setArmed = (on) => {
    if (state.armed === on) return;
    state.armed = on;
    setRootFlag('data-bc-model-3d-armed', on);
    syncTouchAction();
  };

  const disarm = () => {
    if (!state.armed) return;
    setArmed(false);
    state.viewer?.home();
  };

  /* ------------------------------------------------------------------ *
   * The installed-webbing figures — the overlay's picker
   *
   * A hardware product carrying `custom.installed_diagram` has a set of BAKED
   * GLBs of itself with a webbing threaded through it, one per compatible
   * webbing (plus a generic one per pairing) — the same models the doc panel's
   * Compatible-webbings figure draws. snippets/bc-model-3d-installed.liquid
   * resolves which model each webbing renders and hands this file a flat map,
   * so nothing in here knows about pairings, width rules or rosters: a chip
   * names a webbing, the webbing names a model, the model has a url.
   *
   * IN THE OVERLAY ONLY, and the overlay puts the device back on the way out
   * (see `collapse`): the inline slide is the hero product shot, cross-fading
   * over a poster that is a still of the bare device.
   * ------------------------------------------------------------------ */

  /**
   * The config, parsed once. Read from the gallery's own script tag rather than
   * the cloned control bar, so it is available before the overlay exists (and
   * survives the gallery being replaced on a variant change, since the clone
   * on <body> keeps whatever this returned).
   */
  const installedConfig = () => {
    if (state.installed !== null) return state.installed || null;
    const script = document.querySelector('script[type="application/json"][data-bc-model-3d-installed]');
    let config = null;
    try {
      config = JSON.parse(script?.textContent || '');
    } catch (_e) {
      config = null;
    }
    // `false` rather than null, so a page without the control never re-queries.
    state.installed = config?.models && config?.webbings ? config : false;
    return state.installed || null;
  };

  /** The url a key loads from: the device's metafield, or a figure's config entry. */
  const srcFor = (key) => (key === DEVICE_KEY ? state.config?.src : state.installed?.models?.[key]?.src);

  /**
   * A model's BYTES, kept apart from parsing them — the download needs only the
   * network. The map is a HANDOFF, not a cache: an entry lives from the moment
   * the fetch starts until it is parsed (or fails), and never a turn longer; a
   * multi-MB ArrayBuffer sitting in a map is the same leak the model cap exists
   * to prevent. Re-picking an evicted figure refetches, which is a browser
   * HTTP-cache read.
   */
  const fetchGlb = (key) => {
    const held = state.glbBytes.get(key);
    if (held) return held;
    const bytes = fetchModel(srcFor(key));
    // Marks the rejection handled without consuming it — every consumer still
    // awaits `bytes` itself.
    bytes.catch(() => {});
    state.glbBytes.set(key, bytes);
    return bytes;
  };

  /** Most recently picked LAST — #order is only ever read front to back by trim. */
  const touch = (key) => {
    const at = state.order.indexOf(key);
    if (at !== -1) state.order.splice(at, 1);
    state.order.push(key);
  };

  /**
   * Give a retired model's GPU memory back. three uploads geometry buffers and
   * textures lazily and holds them until it is TOLD to let go: dropping the last
   * JS reference frees the JS objects and none of the VRAM.
   *
   * Geometries and materials are shared BETWEEN meshes inside a GLB, so the walk
   * carries a `seen` set. Texture slots are ordinary material properties whose
   * names vary by material type, so they are found by shape rather than from a
   * hardcoded list. Deliberately NOT disposed: the scene environment — it is the
   * renderer's, baked once, and every model that follows reflects it.
   */
  const disposeTree = (root) => {
    const environment = state.viewer?.scene.environment || null;
    const seen = new Set();
    root.removeFromParent?.();
    root.traverse((node) => {
      const geometry = /** @type {any} */ (node).geometry;
      if (geometry?.dispose && !seen.has(geometry)) {
        seen.add(geometry);
        geometry.dispose();
      }
      const material = /** @type {any} */ (node).material;
      if (!material) return;
      for (const one of Array.isArray(material) ? material : [material]) {
        if (!one || seen.has(one)) continue;
        seen.add(one);
        for (const value of Object.values(one)) {
          if (/** @type {any} */ (value)?.isTexture && value !== environment && !seen.has(value)) {
            seen.add(value);
            /** @type {any} */ (value).dispose();
          }
        }
        one.dispose?.();
      }
    });
  };

  /**
   * Bring the cache back to MODEL_CACHE_LIMIT, oldest pick first.
   *
   * Three things are never evicted: the DEVICE (it is the way back, and the
   * model the overlay collapses to), the model on stage, and a key a parse is
   * still filling in. Run only from the showModel call holding the current
   * token, so any other pending show is by definition a stale one.
   */
  const trim = () => {
    for (let i = 0; i < state.order.length; ) {
      const key = state.order[i];
      const entry = state.entries.get(key);
      if (!entry) {
        state.order.splice(i, 1); // already gone; keep the order list honest
        continue;
      }
      if (state.entries.size <= MODEL_CACHE_LIMIT) break;
      if (key === DEVICE_KEY || key === state.modelKey || state.parsing.has(key)) {
        i++;
        continue;
      }
      state.entries.delete(key);
      state.order.splice(i, 1);
      disposeTree(entry.model);
      entry.byName.clear();
      entry.materials.length = 0;
    }
    // Bytes nobody is going to parse — a pick made while a fetch was in flight
    // strands them. Bytes still attached to a live parse are not that (and are
    // released by the parse itself the moment it lands).
    for (const key of [...state.glbBytes.keys()]) if (!state.parsing.has(key)) state.glbBytes.delete(key);
  };

  /** A model, parsed and ready to mount. Concurrent pickers share one promise. */
  const loadEntry = (key) => {
    const cached = state.entries.get(key);
    if (cached) {
      touch(key);
      return Promise.resolve(cached);
    }
    const inFlight = state.parsing.get(key);
    if (inFlight) return inFlight;

    const lib = state.viewer?.lib;
    const src = srcFor(key);
    if (!lib || !src) return Promise.reject(new Error(`bc-model-3d: no model for ${key}`));

    const bytes = fetchGlb(key);
    // Identity-checked, so a retry that has already started its own fetch is
    // never cleared out from under itself by the previous attempt's cleanup.
    const release = () => {
      if (state.glbBytes.get(key) === bytes) state.glbBytes.delete(key);
    };
    const promise = bytes
      .then(
        (buffer) =>
          new Promise((resolve, reject) => {
            const loader = new lib.gltfMod.GLTFLoader();
            if (lib.meshopt) loader.setMeshoptDecoder(lib.meshopt);
            loader.parse(buffer, resourcePath(src), resolve, reject);
          })
      )
      .then((gltf) => {
        const entry = makeEntry(lib.THREE, gltf, viewFrom(state.installed?.models?.[key]?.view));
        state.entries.set(key, entry);
        touch(key);
        state.parsing.delete(key);
        release();
        return entry;
      });
    // A failed fetch must not poison the key: without this the rejected promise
    // stays in the map and every later pick replays the same failure without
    // ever retrying — and the bytes go with it, so the retry is a real retry
    // rather than a re-parse of whatever came back.
    promise.catch(() => {
      state.parsing.delete(key);
      release();
    });
    state.parsing.set(key, promise);
    return promise;
  };

  /**
   * What a chip picks. Two kinds: a DEVICE VIEW names a model key
   * (`data-bc-m3d-pick-model` — the hardware on its own in some state worth
   * seeing, the Double Pin's spacers fitted), anything else names a webbing
   * handle, empty being the bare device. One shape so everything below compares
   * them the same way.
   */
  const pickOf = (chip) => {
    const view = chip.getAttribute('data-bc-m3d-pick-model');
    if (view) return { webbing: null, view };
    return { webbing: chip.getAttribute('data-bc-m3d-pick') || null, view: null };
  };

  const samePick = (a, b) => !!a && !!b && a.webbing === b.webbing && a.view === b.view;

  /** Which chip reads as pressed, and which one is waiting on a GLB. */
  const syncChips = (busy) => {
    const current = { webbing: state.webbing, view: state.deviceView };
    for (const chip of document.querySelectorAll('[data-bc-m3d-pick], [data-bc-m3d-pick-model]')) {
      const pick = pickOf(chip);
      chip.setAttribute('aria-pressed', samePick(pick, current) ? 'true' : 'false');
      if (samePick(pick, busy)) chip.setAttribute('aria-busy', 'true');
      else chip.removeAttribute('aria-busy');
    }
    const dialog = state.dialog;
    if (!dialog) return;
    if (busy) dialog.setAttribute('data-bc-m3d-swapping', '');
    else dialog.removeAttribute('data-bc-m3d-swapping');
  };

  /**
   * THE PICK, as `pickOf` shapes it: a webbing handle, a device view's model
   * key, or neither — which is the bare device.
   *
   * The model already on stage stays there while the new one is fetched — an
   * empty stage says less, and the dim plus the chip's spinner already say the
   * press registered. A pick made while a GLB is in flight wins (`showToken`),
   * so tapping along a row of chips lands on the last one pressed rather than on
   * whichever download happened to finish last.
   */
  const applyPick = async (next) => {
    const config = installedConfig();
    const viewer = state.viewer;
    if (!config || !viewer?.ready) return;
    const key = next.view || (next.webbing ? config.webbings?.[next.webbing]?.model : DEVICE_KEY);
    // A chip for a webbing, or a view, the config does not describe.
    if (key == null) return;
    if (next.view && !config.models?.[next.view]?.src) return;

    const previous = { webbing: state.webbing, view: state.deviceView };
    state.webbing = next.webbing;
    state.deviceView = next.view;
    const token = ++state.showToken;

    if (key === state.modelKey) {
      // Two webbings can share a GENERIC model, and there the repaint below is
      // exactly what has to happen.
      syncChips(null);
      paintWebbing();
      return;
    }

    // Only a model that has to be fetched dims the stage: a cached one resolves
    // in a microtask, and flagging it would blink the overlay on every pick.
    syncChips(state.entries.has(key) ? null : next);

    let entry;
    try {
      entry = await loadEntry(key);
    } catch (_e) {
      // Keep whatever is on screen and put the picker back where it was: a
      // figure whose GLB will not load is not a reason to empty the overlay.
      if (token === state.showToken) {
        state.webbing = previous.webbing;
        state.deviceView = previous.view;
        syncChips(null);
      }
      return;
    }
    if (token !== state.showToken) {
      trim(); // a later pick owns the stage; these bytes are nobody's
      return;
    }

    // Device <-> figure is a different subject and re-homes; figure -> figure is
    // the same assembly in another webbing and keeps the shopper's view. A
    // DEVICE VIEW is one of the figures' own assemblies with nothing threaded
    // through it — same frame, same authored pose — so it sits on the figure
    // side of that line, and only the step to or from the gallery model (posed
    // for the product shot) swings the camera.
    const rehome = (state.modelKey === DEVICE_KEY) !== (key === DEVICE_KEY);
    viewer.mount(entry, rehome);
    state.modelKey = key;
    // After modelKey moves, never before: what is on stage is what trim refuses
    // to evict, and evicting the model just mounted would dispose the buffers
    // the very next render draws from.
    trim();
    recolor();
    paintWebbing();
    viewer.resize();
    syncChips(null);
  };

  /* ------------------------------------------------------------------ *
   * Full-window overlay
   *
   * Not the Fullscreen API: iOS Safari does not support it for non-video
   * elements. A fixed <dialog> opened with showModal() works everywhere and
   * brings the focus trap, the Escape handling and the top layer with it.
   *
   * The dialog is cloned out of the <template> the gallery renders and parked on
   * <body>, because the gallery itself is replaced on every variant change.
   * ------------------------------------------------------------------ */

  const ensureDialog = () => {
    if (state.dialog?.isConnected) return state.dialog;

    const template = document.querySelector('template[data-bc-model-3d-dialog]');
    const dialog = template?.content.firstElementChild?.cloneNode(true);
    if (!(dialog instanceof HTMLDialogElement)) return null;

    // The installed-webbing picker, on the products that have figures to offer
    // (see snippets/bc-model-3d-installed.liquid — no template, no control, and
    // the overlay is the device-only one it has always been). It goes in ABOVE
    // the gesture hint, which is the stage's caption: stage, picker, hint, the
    // same order the doc panel's own overlay uses.
    const controls = /** @type {HTMLTemplateElement|null} */ (
      document.querySelector('template[data-bc-model-3d-installed-controls]')
    );
    const bar = controls?.content.firstElementChild?.cloneNode(true);
    if (bar && installedConfig()) dialog.insertBefore(bar, dialog.querySelector('.bc-model-3d-dialog__hint'));

    document.body.appendChild(dialog);
    dialog.querySelector('[data-bc-model-3d-close]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('close', collapse);
    // The `close` event is the one signal that cannot be relied on (absent in
    // at least one Chrome build — see bc-installed-diagram / bc-rig-figure).
    // Now that `expanded` also keeps the render loop alive, a missed close
    // would pin the loop for the life of the page, so watch the attribute too.
    if (window.MutationObserver) {
      new MutationObserver(() => { if (!dialog.open) collapse(); })
        .observe(dialog, { attributes: true, attributeFilter: ['open'] });
    }

    state.dialog = dialog;
    return dialog;
  };

  const expand = async () => {
    const dialog = ensureDialog();
    const target = dialog?.querySelector('[data-bc-model-3d-stage]');
    if (!dialog || !target) return;

    // The expand control only exists once the model is armed, so the viewer is
    // always already here — but expanding IS another explicit ask, so if it ever
    // is not, load it rather than doing nothing.
    const viewer = state.viewer || (await load(state.host || visibleHost()));
    if (!viewer || !state.stage) return;

    state.expanded = true;
    target.appendChild(state.stage);
    dialog.showModal();
    lockScroll(true);
    syncTouchAction();
    syncHostStates();
    // The overlay outranks every host: its stage sits on <body>, so the loop
    // has to run for it whatever the gallery behind it is doing.
    syncLoop();
    // The ResizeObserver catches up on its own, but not before first paint.
    requestAnimationFrame(() => viewer.resize());
    /** @type {HTMLElement|null} */ (dialog.querySelector('[data-bc-model-3d-close]'))?.focus({
      preventScroll: true,
    });
  };

  /** Hands the stage back to whichever host is currently on screen. */
  const collapse = () => {
    if (!state.expanded) return;
    // A `close` event is delivered as a QUEUED TASK, not synchronously — and a
    // background tab throttles task queues hard. So a close followed by a
    // re-open can deliver the first close's event to an overlay that is open
    // again, which used to hand the stage back to the gallery from underneath
    // an open dialog (an empty overlay, and the model behind it). Neither
    // caller can legitimately reach here with the dialog open: the `close`
    // listener runs after `open` is gone, and the MutationObserver checks it.
    if (state.dialog?.open) return;
    state.expanded = false;
    lockScroll(false);

    // The DEVICE goes back on the stage on the way out. The slide the stage is
    // about to land in is the hero product shot — its poster is a still of the
    // bare device and its thumbnail is that same picture — so a figure left
    // mounted would disagree with everything around it. The device is pinned in
    // the cache, so this is a frame, not a fetch.
    if (state.webbing !== null || state.deviceView !== null) applyPick({ webbing: null, view: null });

    state.viewer?.homeFraming();

    const host = visibleHost();
    if (host && state.stage) {
      host.shadowRoot?.appendChild(state.stage);
      state.host = host;
      requestAnimationFrame(() => state.viewer?.resize());
    }
    syncTouchAction();
    syncHostStates();
    // Back in the gallery, so the hosts' own answer governs again.
    syncLoop();

    // Return focus to whichever control opened the overlay.
    const root = host?.closest('[data-bc-model-3d-root]');
    /** @type {HTMLElement|null} */ (
      root?.querySelector(state.armed ? '[data-bc-model-3d-expand]' : '[data-bc-model-3d-arm]')
    )?.focus({ preventScroll: true });
  };

  /**
   * Horizon locks root scrolling with an attribute on <html> (see base.css
   * `html[scroll-lock]`). Its owner bookkeeping lives in a module a classic
   * script cannot import, so mirror the attribute — and never clear it while
   * some other dialog still wants it.
   */
  const lockScroll = (on) => {
    if (on) {
      document.documentElement.setAttribute('scroll-lock', '');
      return;
    }
    if (document.querySelector('dialog[open][scroll-lock]')) return;
    document.documentElement.removeAttribute('scroll-lock');
  };

  /* ------------------------------------------------------------------ */

  /**
   * Builds the viewer once. Concurrent callers share the one promise.
   *
   * Reached only through `load()` — see the file header: every boot on this page
   * is a shopper pressing a control that says so.
   */
  const boot = (host) =>
    (state.bootPromise ||= buildViewer(host)
      .then((viewer) => {
        if (!viewer) {
          setErrorState();
          return null;
        }
        recolor();
        return viewer;
      })
      .catch((_error) => {
        setErrorState();
        return null;
      }));

  /**
   * Puts the single live stage into whichever host is on screen RIGHT NOW.
   *
   * Split out of `claim()` because the two callers want different things from
   * the cross-fade: a claim is a RE-claim (see markInstant), while this is also
   * how the very first reveal lands — and that one keeps its fade.
   *
   * Deliberately asks `visibleHost()` rather than trusting the caller: both
   * hosts exist at every viewport and which one is displayed is a stylesheet
   * decision, not a DOM-order one.
   */
  const placeStage = (fallbackHost) => {
    const viewer = state.viewer;
    if (!viewer || !state.stage || state.expanded) return;

    const host = visibleHost() || fallbackHost;
    if (!host) return;
    state.host = host;

    if (state.stage.parentNode !== host.shadowRoot) {
      host.shadowRoot?.appendChild(state.stage);
      // Before resize(), which measures the stage and so flushes style: the
      // stage is opacity 0 in a host that has not been marked ready yet.
      syncHostStates();
      viewer.resize();
    }
    recolor();
    syncHostStates();
  };

  /**
   * Turns the badge into its "fetching" state: a spinner and a word, in place of
   * the icon and its label (brand.css).
   *
   * Flagged on every wrapper and every badge on the page rather than on the one
   * that was pressed — both gallery presentations carry a copy, only one of them
   * is displayed, and the displayed one can change under a resize mid-load.
   * `aria-busy` says the same thing to a screen reader.
   */
  const setLoading = (on) => {
    setRootFlag('data-bc-model-3d-loading', on);
    for (const button of document.querySelectorAll('[data-bc-model-3d-arm]')) {
      if (on) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    }
  };

  /**
   * THE ENTRY POINT: the shopper has asked for the model.
   *
   * Fetches the library and the GLB (in parallel — see fetchModel), shows the
   * loading state while they are in flight, and hands the finished stage to the
   * visible host, where the poster cross-fades away over it. Called from the
   * "View in 3D" press and from the expand control; nothing else in this file
   * calls boot(). Idempotent — a second press awaits the same promise.
   *
   * A failure has already painted itself (setErrorState → the poster stays and
   * the badge disappears), so callers only need the null.
   */
  const load = async (preferredHost) => {
    const host = preferredHost?.isConnected ? preferredHost : visibleHost();
    if (!host) return null;
    state.host ||= host;

    setLoading(true);
    try {
      const viewer = await boot(host);
      if (!viewer) return null;
      placeStage(host);
      return viewer;
    } finally {
      setLoading(false);
    }
  };

  class BcModel3d extends HTMLElement {
    /** @type {IntersectionObserver | null} */
    #observer = null;

    connectedCallback() {
      if (!this.shadowRoot) {
        let config = null;
        try {
          config = JSON.parse(this.querySelector('script[data-bc-model-3d]')?.textContent || '');
        } catch (_e) {
          return; // malformed config — leave the poster in place
        }
        if (!config?.src) return;
        state.config ||= config;

        const root = this.attachShadow({ mode: 'open' });
        // The host is an empty overlay box stretched across the slide by
        // brand.css; the poster is a light-DOM SIBLING (see the snippet), so
        // hiding this element in quick-add leaves a normal image slide behind.
        root.innerHTML = `
          <style>
            :host { display: block; position: absolute; inset: 0; }
            .bc-model-3d-stage {
              position: absolute;
              inset: 0;
              opacity: 0;
              transition: opacity 0.35s ease;
            }
            :host([data-bc-state="ready"]) .bc-model-3d-stage { opacity: 1; }
            canvas { display: block; width: 100%; height: 100%; }
            /* Set from the first re-claim onward — see markInstant(). */
            :host([data-bc-instant]) .bc-model-3d-stage { transition: none; }
            @media (prefers-reduced-motion: reduce) {
              .bc-model-3d-stage { transition: none; }
            }
          </style>
        `;
      }

      // The "View in 3D" control is hidden until this line runs (brand.css keys
      // it off `data-bc-js` on the wrapper), so a browser that never upgrades
      // this element — or a config too broken to render, both of which return
      // above — shows the poster and no dead affordance. The flag goes on the
      // WRAPPER rather than here because the button is a light-DOM sibling.
      this.closest('[data-bc-model-3d-root]')?.setAttribute('data-bc-js', '');

      // Before the stage moves, so the carousel is already sitting on the right
      // slide when the canvas lands in it.
      this.#pinSlide();

      // A `display: none` host never intersects, so this is the "am I the
      // gallery view the shopper can actually see" test — which is how the stage
      // follows the slideshow/grid swap at the 750px breakpoint, and one of the
      // two jobs the observer has left (the other is telling the render loop
      // whether there is anywhere on screen to draw to — see syncLoop). It
      // NEVER boots: pre-boot there is no stage to hold and claim() returns
      // immediately (see the file header — the model loads on a press and on
      // nothing else).
      this.#observer ||= new IntersectionObserver(
        (entries) => {
          const on = entries.some((entry) => entry.isIntersecting);
          setHostOnScreen(this, on);
          if (!on || !state.stage) return;
          this.claim();
        },
        // A little slack so a host that scrolls into view has taken the stage
        // before it is looked at. Vertical only: rootMargin pads the ROOT's
        // rect, never an intermediate overflow clip, so padding the inline axis
        // would buy the carousel's own scrollport nothing.
        { rootMargin: '400px 0px' }
      );
      // On screen until told otherwise, so the first frame after a press never
      // waits on an observer callback; the hidden host's own first callback
      // takes it straight back out again.
      setHostOnScreen(this, true);
      this.#observer.observe(this);

      // The observer's first callback is a frame away, and a variant change
      // replaces the whole gallery — waiting for it would blink the poster back
      // in between the old host detaching and the new one claiming.
      if (state.stage) this.claim();
    }

    disconnectedCallback() {
      this.#observer?.disconnect();
      this.#observer = null;
      // A host that is gone is not on screen, and its observer — the only thing
      // that would ever have said so — has just been disconnected. On a variant
      // change the replacement's own observer puts the loop back a frame later,
      // and the reveal in between is a direct render inside placeStage/resize,
      // so nothing waits on this.
      setHostOnScreen(this, false);
      // Deliberately does NOT reclaim or dispose: on a variant change the
      // replacement gallery's host connects and claims the stage, and while the
      // overlay is open no host may hold it at all. Stopping the loop is not
      // disposal either — `setAnimationLoop(null)` leaves the context, the
      // environment and the parsed model exactly where they are (and `expanded`
      // keeps it running regardless, since the overlay's stage is on <body>).
    }

    /**
     * Where a re-rendered gallery opens.
     *
     * Horizon replaces the whole <media-gallery> on a variant change and the
     * replacement always opens on its FIRST slide — which is ours, the model.
     * That is right for a shopper who was looking at the model, and wrong for
     * one who had swiped off to the photos: stock Horizon would have shown them
     * the newly selected variant's own image, and being thrown back onto the
     * model instead is a place they did not ask to go. The server has already
     * sorted that image to the front of `sorted_media`, so it is simply the
     * slide after the model.
     *
     *   was on the MODEL, or has
     *   not moved, or a fresh page  ->  slide 0, the model (nothing to do)
     *   was on a PHOTO              ->  the slide after it, the new variant's
     *                                   photo — stock Horizon's landing
     *
     * UN-BOOTED IS THE COMMON CASE and needs no special handling: the model's
     * slide is then just the poster, which IS the selected variant's photo, and
     * a shopper who never left it has `lastSlideIndex` 0 — equal to the model's
     * own slide, so nothing is pinned and nothing is booted by any of this.
     * `lastSlideIndex` only ever moves on a user-initiated select.
     *
     * Written against `data-bc-model-slide` rather than the constant 0 so the
     * model's position stays one decision made in one place; a model that was
     * not first would leave the replacement opening on a photo already, and the
     * pin would have nothing to do. Galleries inside a dialog (quick add) are a
     * different product context and never take part.
     */
    #pinSlide() {
      const slideshow = /** @type {any} */ (this.closest('slideshow-component'));
      if (!slideshow || this.closest('dialog')) return;

      // Read as a string first: `Number(null)` is 0, which is a perfectly
      // legitimate value here, so an absent attribute has to be told apart from
      // the model leading the gallery.
      const attribute = this.closest('media-gallery')?.getAttribute('data-bc-model-slide');
      const modelSlide = Number(attribute);
      if (attribute == null || !Number.isInteger(modelSlide) || modelSlide < 0) return;
      // Still on the model — where the replacement opens by itself.
      if (state.lastSlideIndex === modelSlide) return;

      // The first slide that is not the model's. Zero means the photos already
      // lead and the gallery lands on the right one unaided. (A model-only
      // product cannot reach here: its single slide is the model's.)
      const photoSlide = modelSlide === 0 ? 1 : 0;
      if (photoSlide === 0) return;

      // `initial-slide` is Horizon's own "open here" contract (slideshow.js
      // reads it in both its deferred setup and its attributeChangedCallback),
      // so setting it stops that setup re-selecting slide 0 from under us —
      // and covers the case where the scroller is not wired up yet and the
      // select() below no-ops. The select() itself is the same decision taken
      // NOW, a microtask before paint, so the photo is the FIRST thing painted.
      slideshow.setAttribute('initial-slide', String(photoSlide));
      slideshow.select?.(photoSlide, undefined, { animate: false });
    }

    /**
     * Takes the live stage for whichever host is on screen — a gallery Horizon
     * re-rendered, a resize across the 750px breakpoint, the overlay handing the
     * stage back. Never builds anything: pre-boot there is nothing to take, and
     * the model is built only when the shopper presses for it (see `load()`).
     *
     * Deliberately does NOT hand the stage to `this`: both hosts claim during
     * first paint and which wins that race is an accident of ordering, not of
     * visibility. `visibleHost()`, inside placeStage, settles it.
     */
    async claim() {
      if (!state.bootPromise) return; // nothing built, and nothing building

      // Every claim is a RE-claim — the model is already there to be shown, so
      // the poster/stage cross-fade has nothing left to reveal and would only
      // flash the photo back. (The first reveal goes through placeStage
      // directly, which is what keeps its fade.) Marked before `visibleHost()`,
      // which flushes style.
      if (state.viewer?.ready) markInstant();

      state.host ||= this;
      if (state.expanded) return; // the overlay outranks every host

      const viewer = state.viewer || (await state.bootPromise);
      if (!viewer) return;

      placeStage(this);
    }
  }

  customElements.define('bc-model-3d', BcModel3d);

  document.addEventListener('click', (event) => {
    const target = /** @type {Element|null} */ (event.target);
    if (!target?.closest) return;

    // The overlay's installed-webbing picker. Delegated from the document like
    // everything else here, because the control bar lives on <body> with the
    // dialog it was cloned into. An empty value is the "Device only" chip.
    const pick = target.closest('[data-bc-m3d-pick], [data-bc-m3d-pick-model]');
    if (pick) {
      event.preventDefault();
      applyPick(pickOf(pick));
      return;
    }

    const armButton = target.closest('[data-bc-model-3d-arm]');
    if (armButton) {
      event.preventDefault();
      // Pressed on a live model this is the old gesture gate and nothing else.
      if (state.viewer?.ready) {
        setArmed(true);
        return;
      }
      // Pressed on the poster it is the LOADER — the one place the page fetches
      // three.js and the GLB. Arming on the far side of it is not a second
      // decision: the shopper asked to handle the model, so it is handed over
      // ready to handle. A press while it is already loading joins the same
      // promise; a failure leaves the poster and takes the badge away.
      const host = armButton.closest('[data-bc-model-3d-root]')?.querySelector('bc-model-3d');
      load(/** @type {any} */ (host)).then((viewer) => {
        if (viewer) setArmed(true);
      });
      return;
    }
    if (target.closest('[data-bc-model-3d-expand]')) {
      event.preventDefault();
      expand();
      return;
    }
    if (target.closest('[data-bc-model-3d-disarm]')) {
      event.preventDefault();
      disarm();
      return;
    }
    // Tapping anywhere off the model releases it, so the carousel gets its swipe
    // back without the shopper having to find the × first.
    if (state.armed && !state.expanded && !target.closest('[data-bc-model-3d-root]')) disarm();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && state.armed && !state.expanded) disarm();
  });

  // A backgrounded tab gets no rAF callbacks anyway in every current browser,
  // but "anyway" is the browser's promise, not ours: a loaded model left in a
  // background tab should hold nothing but memory, and this is what says so.
  document.addEventListener('visibilitychange', syncLoop);

  /**
   * Keep track of where in the carousel the shopper is, so #pinSlide can put a
   * re-rendered gallery back there. Horizon's `slideshow:select` bubbles and
   * covers both the controls and free swipes (`trigger: 'scroll'`).
   *
   * Read off the LIVE event rather than the outgoing element in
   * disconnectedCallback, so it does not matter whether the DOM removes the old
   * gallery before or after it inserts the new one.
   *
   * `userInitiated` is the whole point — this is a record of where the SHOPPER
   * went, not of where the slideshow has been. It excludes every machine
   * select, including the one slideshow.js fires while setting itself up on a
   * `display: none` carousel (the desktop grid presentation still renders one),
   * whose index is an artifact of a zero-size scroller.
   */
  document.addEventListener('slideshow:select', (event) => {
    const target = /** @type {Element|null} */ (event.target);
    if (!target?.closest?.('media-gallery') || target.closest('dialog')) return;
    const detail = /** @type {any} */ (event).detail;
    if (!detail?.userInitiated) return;
    const index = Number(detail.index);
    if (Number.isFinite(index)) state.lastSlideIndex = index;
  });

  // Same pair bc-variant-recolor listens on: `change` catches the pick itself
  // (fired before any morph) and `variant:update` covers picker morphs that
  // replace the inputs. Under Horizon v4 the latter is re-emitted from Shopify's
  // hosted events by assets/bc-events-compat.js. Recolor is driven off the one
  // viewer, so it keeps working while the overlay is open.
  const refresh = () => {
    if (state.viewer?.ready) recolor();
  };
  document.addEventListener('change', (event) => {
    if (/** @type {Element} */ (event.target)?.closest?.('fieldset.variant-option, .variant-option--dropdowns')) {
      refresh();
    }
  });
  document.addEventListener('variant:update', refresh);
})();
